import { dbService } from '../config/supabase';

// Data service that provides a unified interface for both mock and real data
class DataService {
  constructor() {
    this.useSupabase = process.env.REACT_APP_USE_SUPABASE === 'true' || true; // Force Supabase usage
  }

  // Food Items
  async getFoodItems() {
    if (this.useSupabase) {
      return await dbService.getFoodItems();
    } else {
      const { foodItems } = await import('../data/mockData');
      return foodItems;
    }
  }

  async getFoodItemById(id) {
    if (this.useSupabase) {
      return await dbService.getFoodItemById(id);
    } else {
      const { foodItems } = await import('../data/mockData');
      return foodItems.find(item => item.id === parseInt(id));
    }
  }

  // Grocery Items
  async getGroceryItems() {
    if (this.useSupabase) {
      return await dbService.getGroceryItems();
    } else {
      const { groceryItems } = await import('../data/mockData');
      return groceryItems;
    }
  }

  // Pharmacy: separate tables and flows
  async getPharmacies(countryName = null, cityName = null) {
    if (this.useSupabase) {
      return await dbService.getPharmacies(countryName, cityName);
    } else {
      const { pharmacies } = await import('../data/mockData');
      return pharmacies || [];
    }
  }

  async getPharmacyById(id) {
    if (this.useSupabase) {
      return await dbService.getPharmacyById(id);
    } else {
      const { pharmacies } = await import('../data/mockData');
      return (pharmacies || []).find(p => p.id === parseInt(id));
    }
  }

  async getPharmacyItems() {
    if (this.useSupabase) {
      return await dbService.getPharmacyItems();
    } else {
      const { pharmacyItems } = await import('../data/mockData');
      return pharmacyItems || [];
    }
  }

  async getPharmacyItemById(id) {
    if (this.useSupabase) {
      return await dbService.getPharmacyItemById(id);
    } else {
      const { pharmacyItems } = await import('../data/mockData');
      return (pharmacyItems || []).find(item => item.id === parseInt(id));
    }
  }

  // Clothing Stores
  async getClothingStores(countryName = null, cityName = null) {
    if (this.useSupabase) {
      return await dbService.getClothingStores(countryName, cityName);
    } else {
      return [];
    }
  }

  async getClothingStoreById(id) {
    if (this.useSupabase) {
      return await dbService.getClothingStoreById(id);
    } else {
      return null;
    }
  }

  async getClothingItems() {
    if (this.useSupabase) {
      return await dbService.getClothingItems();
    } else {
      return [];
    }
  }

  async getClothingItemById(id) {
    if (this.useSupabase) {
      return await dbService.getClothingItemById(id);
    } else {
      return null;
    }
  }

  // Accessories Stores
  async getAccessoriesStores(countryName = null, cityName = null) {
    if (this.useSupabase) {
      return await dbService.getAccessoriesStores(countryName, cityName);
    } else {
      return [];
    }
  }

  async getAccessoriesStoreById(id) {
    if (this.useSupabase) {
      return await dbService.getAccessoriesStoreById(id);
    } else {
      return null;
    }
  }

  async getAccessoriesItems() {
    if (this.useSupabase) {
      return await dbService.getAccessoriesItems();
    } else {
      return [];
    }
  }

  async getAccessoriesItemById(id) {
    if (this.useSupabase) {
      return await dbService.getAccessoriesItemById(id);
    } else {
      return null;
    }
  }

  // Electronics Stores
  async getElectronicsStores(countryName = null, cityName = null) {
    if (this.useSupabase) {
      return await dbService.getElectronicsStores(countryName, cityName);
    } else {
      return [];
    }
  }

  async getElectronicsStoreById(id) {
    if (this.useSupabase) {
      return await dbService.getElectronicsStoreById(id);
    } else {
      return null;
    }
  }

  async getElectronicsItems() {
    if (this.useSupabase) {
      return await dbService.getElectronicsItems();
    } else {
      return [];
    }
  }

  async getElectronicsItemById(id) {
    if (this.useSupabase) {
      return await dbService.getElectronicsItemById(id);
    } else {
      return null;
    }
  }

  async getGroceryItemById(id) {
    if (this.useSupabase) {
      return await dbService.getGroceryItemById(id);
    } else {
      const { groceryItems } = await import('../data/mockData');
      return groceryItems.find(item => item.id === parseInt(id));
    }
  }

  // Categories
  async getFoodCategories() {
    if (this.useSupabase) {
      return await dbService.getFoodCategories();
    } else {
      const { foodCategories } = await import('../data/mockData');
      return foodCategories;
    }
  }

  async getGroceryCategories() {
    if (this.useSupabase) {
      return await dbService.getGroceryCategories();
    } else {
      const { groceryCategories } = await import('../data/mockData');
      return groceryCategories;
    }
  }

  // Restaurants
  async getRestaurants(countryName = null, cityName = null) {
    if (this.useSupabase) {
      return await dbService.getRestaurants(countryName, cityName);
    } else {
      const { restaurants } = await import('../data/mockData');
      return restaurants;
    }
  }

  async getRestaurantById(id) {
    if (this.useSupabase) {
      return await dbService.getRestaurantById(id);
    } else {
      const { restaurants } = await import('../data/mockData');
      return restaurants.find(restaurant => restaurant.id === parseInt(id));
    }
  }

  // Shops
  async getShops(countryName = null, cityName = null) {
    if (this.useSupabase) {
      return await dbService.getShops(countryName, cityName);
    } else {
      const { shops } = await import('../data/mockData');
      return shops;
    }
  }

  async getShopById(id) {
    if (this.useSupabase) {
      return await dbService.getShopById(id);
    } else {
      const { shops } = await import('../data/mockData');
      return shops.find(shop => shop.id === parseInt(id));
    }
  }

  // Customers
  async getCustomers() {
    if (this.useSupabase) {
      return await dbService.getCustomers();
    } else {
      const { users } = await import('../data/mockData');
      return users.customers;
    }
  }

  async getCustomerById(id) {
    if (this.useSupabase) {
      return await dbService.getCustomerById(id);
    } else {
      const { users } = await import('../data/mockData');
      return users.customers.find(customer => customer.id === parseInt(id));
    }
  }

  async createCustomer(customerData) {
    if (this.useSupabase) {
      return await dbService.createCustomer(customerData);
    } else {
      // Mock implementation
      const newCustomer = {
        id: Date.now(),
        ...customerData,
        created_at: new Date().toISOString()
      };
      return newCustomer;
    }
  }

  async getCustomerByEmail(email) {
    if (this.useSupabase) {
      return await dbService.getCustomerByEmail(email);
    } else {
      const { users } = await import('../data/mockData');
      return users.customers.find(customer => customer.email === email);
    }
  }

  async getCustomerByAuthUid(authUid) {
    if (this.useSupabase) {
      return await dbService.getCustomerByAuthUid(authUid);
    } else {
      // Mock mode: not supported, return null to indicate not found
      return null;
    }
  }

  async createOrUpdateCustomerByEmail(email, customerData) {
    if (this.useSupabase) {
      return await dbService.createOrUpdateCustomerByEmail(email, customerData);
    } else {
      // Mock implementation
      const existingCustomer = await this.getCustomerByEmail(email);
      if (existingCustomer) {
        return await this.updateCustomer(existingCustomer.id, customerData);
      } else {
        return await this.createCustomer({ ...customerData, email });
      }
    }
  }

  async updateCustomer(id, customerData) {
    if (this.useSupabase) {
      return await dbService.updateCustomer(id, customerData);
    } else {
      // Mock implementation
      return { id, ...customerData, updated_at: new Date().toISOString() };
    }
  }

  async updateCustomerByEmail(email, customerData) {
    if (this.useSupabase) {
      return await dbService.updateCustomerByEmail(email, customerData);
    } else {
      // Mock implementation
      const customer = await this.getCustomerByEmail(email);
      if (customer) {
        return { ...customer, ...customerData, updated_at: new Date().toISOString() };
      }
      return null;
    }
  }

  // Orders
  async getOrders() {
    if (this.useSupabase) {
      return await dbService.getOrders();
    } else {
      const { orders } = await import('../data/mockData');
      return orders;
    }
  }

  async getOrderById(id) {
    if (this.useSupabase) {
      return await dbService.getOrderById(id);
    } else {
      const { orders } = await import('../data/mockData');
      return orders.find(o => o.id === parseInt(id));
    }
  }

  async getOrdersByCustomer(customerId) {
    if (this.useSupabase) {
      return await dbService.getOrdersByCustomer(customerId);
    } else {
      const { orders } = await import('../data/mockData');
      return orders.filter(order => order.customerId === parseInt(customerId));
    }
  }

  async createOrder(orderData) {
    if (this.useSupabase) {
      return await dbService.createOrder(orderData);
    } else {
      // Mock implementation
      const newOrder = {
        id: Date.now(),
        ...orderData,
        created_at: new Date().toISOString()
      };
      return newOrder;
    }
  }

  async updateOrderStatus(id, status) {
    if (this.useSupabase) {
      return await dbService.updateOrderStatus(id, status);
    } else {
      // Mock implementation
      return { id, status, updated_at: new Date().toISOString() };
    }
  }

  // Order Items
  async createOrderItems(orderItems) {
    if (this.useSupabase) {
      return await dbService.createOrderItems(orderItems);
    } else {
      // Mock implementation
      return orderItems.map((item, index) => ({
        id: Date.now() + index,
        ...item,
        created_at: new Date().toISOString()
      }));
    }
  }

  // Promotions
  async getPromotions() {
    if (this.useSupabase) {
      return await dbService.getPromotions();
    } else {
      const { promotions } = await import('../data/mockData');
      return promotions;
    }
  }

  // Offers
  async getOffers(section = null, countryCode = null) {
    if (this.useSupabase) {
      return await dbService.getOffers(section, countryCode);
    } else {
      return [];
    }
  }

  // Vendors
  async getVendors() {
    if (this.useSupabase) {
      return await dbService.getVendors();
    } else {
      const { users } = await import('../data/mockData');
      return users.vendors;
    }
  }

  async getVendorById(id) {
    if (this.useSupabase) {
      return await dbService.getVendorById(id);
    } else {
      const { users } = await import('../data/mockData');
      return users.vendors.find(vendor => vendor.id === parseInt(id));
    }
  }

  async createVendor(vendorData) {
    if (this.useSupabase) {
      return await dbService.createVendor(vendorData);
    } else {
      // Mock implementation
      const newVendor = {
        id: Date.now(),
        ...vendorData,
        created_at: new Date().toISOString()
      };
      return newVendor;
    }
  }

  async updateVendor(id, vendorData) {
    if (this.useSupabase) {
      return await dbService.updateVendor(id, vendorData);
    } else {
      // Mock implementation
      return { id, ...vendorData, updated_at: new Date().toISOString() };
    }
  }

  // Vendor Restaurant Management
  async createRestaurant(restaurantData) {
    if (this.useSupabase) {
      return await dbService.createRestaurant(restaurantData);
    } else {
      // Mock implementation
      const newRestaurant = {
        id: Date.now(),
        ...restaurantData,
        created_at: new Date().toISOString()
      };
      return newRestaurant;
    }
  }

  async updateRestaurant(id, restaurantData) {
    if (this.useSupabase) {
      return await dbService.updateRestaurant(id, restaurantData);
    } else {
      // Mock implementation
      return { id, ...restaurantData, updated_at: new Date().toISOString() };
    }
  }

  // Vendor Shop Management
  async createShop(shopData) {
    if (this.useSupabase) {
      return await dbService.createShop(shopData);
    } else {
      // Mock implementation
      const newShop = {
        id: Date.now(),
        ...shopData,
        created_at: new Date().toISOString()
      };
      return newShop;
    }
  }

  async updateShop(id, shopData) {
    if (this.useSupabase) {
      return await dbService.updateShop(id, shopData);
    } else {
      // Mock implementation
      return { id, ...shopData, updated_at: new Date().toISOString() };
    }
  }

  // Vendor Food Item Management
  async createFoodItem(foodItemData) {
    if (this.useSupabase) {
      return await dbService.createFoodItem(foodItemData);
    } else {
      // Mock implementation
      const newFoodItem = {
        id: Date.now(),
        ...foodItemData,
        created_at: new Date().toISOString()
      };
      return newFoodItem;
    }
  }

  async updateFoodItem(id, foodItemData) {
    if (this.useSupabase) {
      return await dbService.updateFoodItem(id, foodItemData);
    } else {
      // Mock implementation
      return { id, ...foodItemData, updated_at: new Date().toISOString() };
    }
  }

  // Vendor Grocery Item Management
  async createGroceryItem(groceryItemData) {
    if (this.useSupabase) {
      return await dbService.createGroceryItem(groceryItemData);
    } else {
      // Mock implementation
      const newGroceryItem = {
        id: Date.now(),
        ...groceryItemData,
        created_at: new Date().toISOString()
      };
      return newGroceryItem;
    }
  }

  async updateGroceryItem(id, groceryItemData) {
    if (this.useSupabase) {
      return await dbService.updateGroceryItem(id, groceryItemData);
    } else {
      // Mock implementation
      return { id, ...groceryItemData, updated_at: new Date().toISOString() };
    }
  }

  // Real-time subscriptions (only available with Supabase)
  subscribeToOrders(callback) {
    if (this.useSupabase) {
      return dbService.subscribeToOrders(callback);
    } else {
      // Mock implementation - no real-time updates
      return { unsubscribe: () => {} };
    }
  }

  subscribeToOrderItems(callback) {
    if (this.useSupabase) {
      return dbService.subscribeToOrderItems(callback);
    } else {
      // Mock implementation - no real-time updates
      return { unsubscribe: () => {} };
    }
  }

  subscribeToShops(callback) {
    if (this.useSupabase) {
      return dbService.subscribeToShops(callback);
    } else {
      return { unsubscribe: () => {} };
    }
  }

  subscribeToGroceryItems(callback) {
    if (this.useSupabase) {
      return dbService.subscribeToGroceryItems(callback);
    } else {
      return { unsubscribe: () => {} };
    }
  }

  subscribeToClothingStores(callback) {
    if (this.useSupabase) {
      return dbService.subscribeToClothingStores(callback);
    } else {
      return { unsubscribe: () => {} };
    }
  }

  subscribeToClothingItems(callback) {
    if (this.useSupabase) {
      return dbService.subscribeToClothingItems(callback);
    } else {
      return { unsubscribe: () => {} };
    }
  }

  subscribeToAccessoriesStores(callback) {
    if (this.useSupabase) {
      return dbService.subscribeToAccessoriesStores(callback);
    } else {
      return { unsubscribe: () => {} };
    }
  }

  subscribeToAccessoriesItems(callback) {
    if (this.useSupabase) {
      return dbService.subscribeToAccessoriesItems(callback);
    } else {
      return { unsubscribe: () => {} };
    }
  }

  subscribeToElectronicsStores(callback) {
    if (this.useSupabase) {
      return dbService.subscribeToElectronicsStores(callback);
    } else {
      return { unsubscribe: () => {} };
    }
  }

  subscribeToElectronicsItems(callback) {
    if (this.useSupabase) {
      return dbService.subscribeToElectronicsItems(callback);
    } else {
      return { unsubscribe: () => {} };
    }
  }

  subscribeToOrder(id, callback) {
    if (this.useSupabase) {
      return dbService.subscribeToOrder(id, callback);
    } else {
      return { unsubscribe: () => {} };
    }
  }

  // Support Tickets
  async createSupportTicket(ticketData) {
    if (this.useSupabase) {
      return await dbService.createSupportTicket(ticketData);
    } else {
      // Mock implementation
      const newTicket = {
        id: Date.now(),
        ...ticketData,
        status: 'open',
        created_at: new Date().toISOString()
      };
      return newTicket;
    }
  }

  async getSupportTicketsByCustomer(customerId) {
    if (this.useSupabase) {
      return await dbService.getSupportTicketsByCustomer(customerId);
    } else {
      // Mock implementation
      return [];
    }
  }

  async updateSupportTicketStatus(id, status) {
    if (this.useSupabase) {
      return await dbService.updateSupportTicketStatus(id, status);
    } else {
      // Mock implementation
      return { id, status, updated_at: new Date().toISOString() };
    }
  }

  // Customer Preferences
  async updateCustomerPreferences(customerId, preferences) {
    if (this.useSupabase) {
      return await dbService.updateCustomerPreferences(customerId, preferences);
    } else {
      // Mock implementation
      return { customerId, preferences, updated_at: new Date().toISOString() };
    }
  }

  async getCustomerPreferences(customerId) {
    if (this.useSupabase) {
      return await dbService.getCustomerPreferences(customerId);
    } else {
      // Mock implementation - default preferences
      return {
        notifications: true,
        emailUpdates: true,
        smsUpdates: false,
        promotions: true
      };
    }
  }
}

// Create and export a singleton instance
export const dataService = new DataService();
export default dataService;
